import { trackAnalytics } from '../services/api';

/**
 * Analytics Utility
 * Provides easy-to-use functions for tracking user interactions
 */

/**
 * Track a view event for a resource
 * @param {string} resourceType - Type of resource: 'ebook', 'video', 'chapter', 'lesson'
 * @param {string} resourceId - ID of the main resource (course/ebook)
 * @param {string} [subId] - Optional ID for chapter or lesson
 * @param {number} [duration] - Optional duration in seconds
 */
export const trackView = async (resourceType, resourceId, subId = null, duration = 0) => {
  try {
    const payload = {
      resourceType,
      resourceId,
      action: 'view',
      duration
    };

    // Add chapter/lesson ID if provided
    if (subId) {
      if (resourceType === 'chapter') {
        payload.chapterId = subId;
      } else if (resourceType === 'lesson') {
        payload.lessonId = subId;
      }
    }

    await trackAnalytics(payload);
    console.log('✅ Analytics tracked:', payload);
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.error('Analytics tracking failed:', error);
  }
};

/**
 * Track a completion event for a resource
 * @param {string} resourceType - Type of resource: 'ebook', 'video', 'chapter', 'lesson'
 * @param {string} resourceId - ID of the main resource (course/ebook)
 * @param {string} [subId] - Optional ID for chapter or lesson
 * @param {number} [duration] - Optional duration in seconds
 */
export const trackCompletion = async (resourceType, resourceId, subId = null, duration = 0) => {
  try {
    const payload = {
      resourceType,
      resourceId,
      action: 'complete',
      duration
    };

    // Add chapter/lesson ID if provided
    if (subId) {
      if (resourceType === 'chapter') {
        payload.chapterId = subId;
      } else if (resourceType === 'lesson') {
        payload.lessonId = subId;
      }
    }

    await trackAnalytics(payload);
    console.log('✅ Analytics tracked:', payload);
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.error('Analytics tracking failed:', error);
  }
};

/**
 * Track duration/progress for a resource
 * @param {string} resourceType - Type of resource: 'ebook', 'video', 'chapter', 'lesson'
 * @param {string} resourceId - ID of the main resource (course/ebook)
 * @param {number} duration - Duration in seconds
 * @param {string} [subId] - Optional ID for chapter or lesson
 */
export const trackDuration = async (resourceType, resourceId, duration, subId = null) => {
  try {
    const payload = {
      resourceType,
      resourceId,
      action: 'view',
      duration: Math.round(duration) // Ensure it's an integer
    };

    // Add chapter/lesson ID if provided
    if (subId) {
      if (resourceType === 'chapter') {
        payload.chapterId = subId;
      } else if (resourceType === 'lesson') {
        payload.lessonId = subId;
      }
    }

    await trackAnalytics(payload);
    console.log('✅ Analytics tracked:', payload);
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.error('Analytics tracking failed:', error);
  }
};

/**
 * Hook for tracking time spent on a page/component
 * Returns a cleanup function to be called when component unmounts
 * @param {string} resourceType - Type of resource
 * @param {string} resourceId - ID of the resource
 * @param {string} [subId] - Optional sub-resource ID
 * @returns {Function} Cleanup function
 */
export const useAnalyticsTimer = (resourceType, resourceId, subId = null) => {
  const startTime = Date.now();

  // Return cleanup function
  return () => {
    const duration = Math.round((Date.now() - startTime) / 1000); // Convert to seconds
    if (duration > 0) {
      trackDuration(resourceType, resourceId, duration, subId);
    }
  };
};

/**
 * Track ebook view
 * @param {string} ebookId - Ebook/Course ID
 * @param {string} [chapterId] - Optional chapter ID
 */
export const trackEbookView = (ebookId, chapterId = null) => {
  if (chapterId) {
    return trackView('chapter', ebookId, chapterId);
  }
  return trackView('ebook', ebookId);
};

/**
 * Track ebook completion
 * @param {string} ebookId - Ebook/Course ID
 * @param {string} [chapterId] - Optional chapter ID
 * @param {number} [duration] - Time spent in seconds
 */
export const trackEbookCompletion = (ebookId, chapterId = null, duration = 0) => {
  if (chapterId) {
    return trackCompletion('chapter', ebookId, chapterId, duration);
  }
  return trackCompletion('ebook', ebookId, null, duration);
};

/**
 * Track video view
 * @param {string} courseId - Video course ID
 * @param {string} [lessonId] - Optional lesson ID
 */
export const trackVideoView = (courseId, lessonId = null) => {
  if (lessonId) {
    return trackView('lesson', courseId, lessonId);
  }
  return trackView('video', courseId);
};

/**
 * Track video completion
 * @param {string} courseId - Video course ID
 * @param {string} [lessonId] - Optional lesson ID
 * @param {number} [duration] - Time spent in seconds
 */
export const trackVideoCompletion = (courseId, lessonId = null, duration = 0) => {
  if (lessonId) {
    return trackCompletion('lesson', courseId, lessonId, duration);
  }
  return trackCompletion('video', courseId, null, duration);
};

/**
 * Track video watch duration
 * @param {string} courseId - Video course ID
 * @param {number} duration - Duration watched in seconds
 * @param {string} [lessonId] - Optional lesson ID
 */
export const trackVideoProgress = (courseId, duration, lessonId = null) => {
  if (lessonId) {
    return trackDuration('lesson', courseId, duration, lessonId);
  }
  return trackDuration('video', courseId, duration);
};

export default {
  trackView,
  trackCompletion,
  trackDuration,
  useAnalyticsTimer,
  trackEbookView,
  trackEbookCompletion,
  trackVideoView,
  trackVideoCompletion,
  trackVideoProgress
};
